import { fetchEventSource } from "@microsoft/fetch-event-source";
import { API_BASE, attemptRefresh } from "./api";
import { tokenStore } from "./tokens";

export type Frame = {
  id?: number;
  device_id: string;
  device_type?: string;
  kind: "hb" | "info" | "status";
  payload: string;
  at: string;
};

export type StreamStatus = "connecting" | "open" | "closed";

type Listener = (f: Frame) => void;
type StatusListener = (s: StreamStatus) => void;

export type StreamHandle = {
  close(): void;
};

export function openStream(
  path: "/stream" | "/stream/unclaimed",
  onFrame: Listener,
  opts: { onStatus?: StatusListener } = {},
): StreamHandle {
  const url = `${API_BASE}${path}`;
  const ctrl = new AbortController();

  const dispatch = (data: string) => {
    try {
      onFrame(JSON.parse(data) as Frame);
    } catch {

    }
  };

  const authedFetch: typeof fetch = async (input, init) => {
    const send = async () => {
      const token = await tokenStore.getAccess();
      const headers = new Headers(init?.headers);
      if (token) headers.set("Authorization", `Bearer ${token}`);
      return fetch(input, { ...init, headers });
    };
    const res = await send();
    if (res.status === 401 && (await attemptRefresh())) {
      return send();
    }
    return res;
  };

  opts.onStatus?.("connecting");

  void (async () => {
    try {
      await fetchEventSource(url, {
        signal: ctrl.signal,
        credentials: tokenStore.isNative ? "omit" : "include",
        openWhenHidden: true,
        fetch: authedFetch,
        async onopen(resp) {
          if (resp.ok) {
            opts.onStatus?.("open");
            return;
          }
          throw new Error(`SSE open ${resp.status}`);
        },
        onmessage(ev) {
          dispatch(ev.data);
        },
        onerror() {
          opts.onStatus?.("connecting");
        },
        onclose() {
          opts.onStatus?.("closed");
        },
      });
    } catch {
      opts.onStatus?.("closed");
    }
  })();

  return {
    close() {
      ctrl.abort();
      opts.onStatus?.("closed");
    },
  };
}
